import TinyColor from "@ctrl/tinycolor";
import { returnFlowJsonFormat } from "@redotech/redo-model/return-flow";
import { createContext, useEffect, useLayoutEffect, useState } from "react";
import { getSettings } from "../api";

const SettingsContext = createContext({
  settings: undefined,
  setSettings: (settings: any) => {},
});

const SettingsContextProvider = ({ children }) => {
  const [settings, setSettings] = useState(undefined);
  const customerSupportRefundStrategy = (then) => {
    if (then == "refund_only") {
      return ["refund"];
    }

    if (then == "green_return") {
      return ["green_return"];
    }

    if (then == "giftcard_only") {
      return ["store_credit"];
    }

    if (then == "exchange_giftcard") {
      return ["exchange", "store_credit"];
    }

    if (then == "continue") {
      return ["continue"];
    }

    if (then == "manual") {
      return ["manual_review"];
    }

    // if we get null, for us it's the same than "continue",
    return null;
  };

  useEffect(() => {
    getSettings().then((response) => {
      const data = response.data;
      setSettings({
        theme: {
          button_background_color: data.theme.button_color,
          button_text_color:
            TinyColor(data.theme.button_color).getBrightness() < 200
              ? "#ffffff"
              : "#000000",
          button_color_dark:
            TinyColor(data.theme.button_color).getBrightness() < 200,
          accent_color: data.theme.accent_color,
          accent_background_color: data.theme.accent_background_color,
          accent_text_color:
            TinyColor(data.theme.accent_color).getBrightness() < 200
              ? "#ffffff"
              : "#000000",
          home_text_color: data.theme.home_text_color,
          background_image: data.theme.background_url,
          logo_image: data.theme.logo_url,
          enable_alert: data.theme.enable_alert,
          notification: data.theme.notification,
          privacy_link: data.theme.privacy_link,
        },
        locations: data.settings.locations,
        restockingFees: data.settings.restockingFees,
        storeUrl: data.storeUrl,
        exchanges: data.settings.exchanges,
        inventory: data.settings.inventory,
        tags: data.settings.tags,
        policy_days: data.settings.policyDays,
        returnFlow:
          data.settings.returnFlow &&
          returnFlowJsonFormat.read(data.settings.returnFlow),
        active: data.service_active,
        qualifying_questions: data.settings.qualifyingQuestions || null,
        returnReasons: data.settings.returnReasons || [],
        returnLabel: data.settings.returnLabel,
        greenReturn: data.settings.greenReturn || null,
        coverage: data.settings.coverage,
        merchantCoverage: data.settings.merchantCoverage,
        wrong_item: {
          ask: data.settings.customerSupport.wrongProduct.ask,
          comments: {
            description:
              data.settings.customerSupport.wrongProduct.ifYes == "message",
            images: data.settings.customerSupport.wrongProduct.ifYes == "image",
          },
          strategy: customerSupportRefundStrategy(
            data.settings.customerSupport.wrongProduct.then,
          ),
        },
        defective: {
          ask: data.settings.customerSupport.damagedItem.ask,
          comments: {
            description:
              data.settings.customerSupport.damagedItem.ifYes == "message",
            images: data.settings.customerSupport.damagedItem.ifYes == "image",
          },
          strategy: customerSupportRefundStrategy(
            data.settings.customerSupport.damagedItem.then,
          ),
        },
      });
    });
  }, []);

  useLayoutEffect(() => {
    if (settings && settings.theme) {
      document.documentElement.style.setProperty(
        "--redo-primary-color",
        settings.theme.button_background_color,
      );
      document.documentElement.style.setProperty(
        "--redo-text-color",
        settings.theme.button_text_color,
      );
      document.documentElement.style.setProperty(
        "--redo-primary-button-color",
        settings.theme.button_background_color,
      );
      document.documentElement.style.setProperty(
        "--redo-accent-color",
        settings.theme.accent_color,
      );
      document.documentElement.style.setProperty(
        "--redo-accent-color-background",
        settings.theme.accent_background_color,
      );
    }
  }, [settings]);

  if (!settings) {
    return <div>Loading</div>;
  }

  return (
    <SettingsContext.Provider value={{ settings, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export { SettingsContext, SettingsContextProvider };
